<template>
  <base-card>
    <div class="tw-flex tw-flex-col">
      <h3 class="tw-text-gray-700 tw-text-lg tw-font-thin tw-whitespace-nowrap tw-mb-5">Revenue Trend <span class="tw-text-sm">(month over month)</span></h3>
      <div class="chart-container tw-self-center">
        <inline-error id="charges-error" :title="errorTitle" :message="errorMessage" :error="error" />
        <div v-if="isLoading" class="tw-flex tw-gap-2 tw-items-end">
          <base-skeleton-loader class="tw-h-6 tw-w-5" />
          <base-skeleton-loader class="tw-h-8 tw-w-5" />
          <base-skeleton-loader class="tw-h-10 tw-w-5" />
          <base-skeleton-loader class="tw-h-16 tw-w-5" />
          <base-skeleton-loader class="tw-h-20 tw-w-5" />
          <base-skeleton-loader class="tw-h-24 tw-w-5" />
          <base-skeleton-loader class="tw-h-36 tw-w-5" />
          <base-skeleton-loader class="tw-h-44 tw-w-5" />
          <base-skeleton-loader class="tw-h-60 tw-w-5" />
          <base-skeleton-loader class="tw-h-72 tw-w-5" />
        </div>
        <bar-chart v-else v-show="errorMessage == ''" :chart-data="chartData" :options="chartOptions" style="height: 20rem" />
      </div>
    </div>
  </base-card>
</template>

<script>
import BarChart from "@/components/charts/BarChart";
import { mapGetters } from "vuex";
import InlineError from "@/components/InlineError";

const formatter = new Intl.NumberFormat(window.navigator.language, {
  style: "currency",
  currency: "USD",
});

export default {
  name: "RevenueTrend",
  components: { BarChart, InlineError },
  props: {
    businessId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      revenueSummary: [],
      isLoading: false,
      windowWidth: window.innerWidth,
      errorTitle: "",
      errorMessage: "",
      error: null,
    };
  },
  computed: {
    ...mapGetters("organization", ["organization", "cobrandId"]),
    isMobile() {
      return this.windowWidth <= 768;
    },
    chartOptions() {
      return {
        maintainAspectRatio: false,
        scales: {
          yAxes: [
            {
              stacked: true,
              ticks: {
                beginAtZero: true,
                callback: (label) => {
                  return formatter.format(label);
                },
              },
            },
          ],
          xAxes: [
            {
              stacked: true,
              ticks: {
                beginAtZero: true,
              },
            },
          ],
        },
        legend: {
          position: "bottom",
        },
        tooltips: {
          callbacks: {
            label(tooltipItem, data) {
              let label = data.datasets[tooltipItem.datasetIndex].label || "";

              if (label) {
                label += ": ";
              }
              label += `${formatter.format(tooltipItem.yLabel)}`;
              return label;
            },
          },
        },
      };
    },
    chartData() {
      return {
        labels: this.revenueSummary ? this.revenueSummary.map((result) => result.monthName) : "",
        datasets: [
          {
            label: "Charged",
            data: this.revenueSummary ? this.revenueSummary.map((result) => result.revenueInCents / 100) : 0,
            borderColor: this.colors.charged.borderColor,
            borderWidth: 0.5,
            backgroundColor: this.colors.charged.backgroundColor,
            order: 1,
          },
          {
            label: "Predicted",
            data: this.revenueSummary ? this.revenueSummary.map((result) => result.predictedRevenueInCents / 100) : 0,
            borderColor: this.colors.predicted.borderColor,
            borderWidth: 0.5,
            backgroundColor: this.colors.predicted.backgroundColor,
            order: 1,
          },
        ],
      };
    },
    colors() {
      switch (this.cobrandId) {
        case "rx-harmony":
          return {
            charged: {
              borderColor: "#49a13e",
              backgroundColor: "rgba(85, 185, 72, 0.7)",
            },
            predicted: {
              borderColor: "#49a13e",
              backgroundColor: "rgba(85, 185, 72, 0.3)",
            },
          };
      }

      return {
        charged: {
          borderColor: "#ff0090", // primary
          backgroundColor: "#ff99d2",
        },
        predicted: {
          borderColor: "#ff0090", // primary
          backgroundColor: "rgba(255, 0, 145, 0.10)",
        },
      };
    },
  },
  created() {
    this.loadPlanCounts();
    window.addEventListener("resize", this.onResize);
  },
  destroyed() {
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    loadPlanCounts() {
      this.isLoading = true;

      let url = `/businesses/${this.businessId}/dashboard/revenue-trend`;

      this.$axios
        .get(url)
        .then((res) => {
          this.revenueSummary = res.data.results;
        })
        .catch(() => {
          this.errorTitle = `Failed to load charges`;
          this.errorMessage = `Failed to load charges`;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onResize() {
      this.windowWidth = window.innerWidth;
    },
  },
};
</script>
